import React, { useMemo, useState } from "react";
import { CloudUpload, Delete, DriveFileRenameOutline, HighlightAlt } from "@mui/icons-material";
import { Alert, Box, Typography } from "@mui/material";

import { useGetVersionStatusQuery } from "fond/api";
import { getCurrentProjectData } from "fond/project";
import { deleteLayerFeatures, hasLayer, openUploadPanel, setActiveLayerGroupId, StatusTypes } from "fond/project/redux";
import { LayerId } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

interface LayerGroupPanelProps {
  title: string;
  navigationKey: string;
  layerIds: LayerId[];
  /**
   * Input layer group whose area select and upload flows the panel uses; omitted for groups uploaded via `${navigationKey}-upload`.
   */
  inputLayerGroupId?: string;
}

/**
 * Entry panel for a planner extended layer group, offering import, draw and delete actions.
 */
const LayerGroupPanel: React.FC<LayerGroupPanelProps> = ({ title, navigationKey, layerIds, inputLayerGroupId }: LayerGroupPanelProps) => {
  const dispatch = useAppDispatch();
  const { open, clear } = useStackedNavigationContext();
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const { data: status } = useGetVersionStatusQuery(versionId, { skip: !versionId || !inputLayerGroupId });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasData = layerIds.some((layerId) => hasLayer(projectData, layerId));

  const handleAreaSelect = () => {
    dispatch(setActiveLayerGroupId(inputLayerGroupId));
    open("area-select");
  };

  const handleUpload = () => {
    if (inputLayerGroupId) {
      dispatch(openUploadPanel(inputLayerGroupId, status?.Status === StatusTypes.Complete));
      open("upload");
    } else {
      open(`${navigationKey}-upload`);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteLayerFeatures(layerIds, navigationKey));
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };
  const InfoText = useMemo(() => {
    if (navigationKey === "extended-design-aerial") {
      return "All possible locations where aerial fiber cables and devices may be placed in the Design step.";
    } else if (navigationKey === "extended-design-underground") {
      return "All possible locations where underground fiber cables and devices may be placed in the Design step.";
    } else if (navigationKey === "extended-design-leadins") {
      return "The approximate service path from the main network to an address location.";
    } else {
      return false;
    }
  }, [navigationKey]);
  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={clear} />
      <Box px={1}>
        {InfoText && (
          <Alert severity="info" sx={{ mb: 2 }} variant="compact" data-testid="info-alert">
            <Typography variant="body3" component="p">
              {InfoText}
            </Typography>
          </Alert>
        )}
        <Box mb={1}>
          <Typography fontWeight="500">Import data</Typography>
          {inputLayerGroupId && (
            <StackedNavigationButton
              data-testid={`area-select-${navigationKey}-button`}
              icon={<HighlightAlt />}
              title="Area Select"
              subtitle="Use FOND data"
              onClick={handleAreaSelect}
            />
          )}
          <StackedNavigationButton
            data-testid={`upload-${navigationKey}-button`}
            icon={<CloudUpload />}
            title="Upload"
            subtitle="Use my data"
            onClick={handleUpload}
          />
        </Box>
        <Box mb={1}>
          <Typography fontWeight="500">Manual selection and edit</Typography>
          <StackedNavigationButton
            data-testid={`draw-${navigationKey}-button`}
            icon={<DriveFileRenameOutline />}
            title="Draw"
            subtitle="Add and edit data on map"
            onClick={() => open(`${navigationKey}-draw`)}
          />
        </Box>
        {hasData && (
          <Box mb={1}>
            <Typography fontWeight="500">Remove existing data</Typography>
            <StackedNavigationButton
              data-testid={`delete-${navigationKey}-button`}
              icon={<Delete />}
              title="Delete"
              subtitle="Remove existing data"
              onClick={() => setShowDeleteConfirm(true)}
            />
            <ConfirmModal
              open={showDeleteConfirm}
              header={`Delete ${title.toLowerCase()}?`}
              confirmText="Delete"
              cancelText="Cancel"
              color="error"
              content={
                <Typography variant="body2">
                  This will permanently delete all existing {title.toLowerCase()} data. This action cannot be undone.
                </Typography>
              }
              onConfirm={handleConfirmDelete}
              onCancel={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LayerGroupPanel;
